import React from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FileTextIcon, ClockIcon, CheckCircleIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
export function RequisitionSummaryReport({
  filters,
  locale
}) {
  // Sample data for requisition summary
  const statusData = [{
    name: 'Draft',
    value: 24
  }, {
    name: 'Pending Approval',
    value: 18
  }, {
    name: 'Approved',
    value: 42
  }, {
    name: 'Rejected',
    value: 8
  }, {
    name: 'Converted to PO',
    value: 38
  }];
  const COLORS = ['#94a3b8', '#f59e0b', '#10b981', '#ef4444', '#3b82f6'];
  const departmentData = [{
    department: 'IT',
    count: 35,
    value: 125000
  }, {
    department: 'Marketing',
    count: 28,
    value: 87500
  }, {
    department: 'Operations',
    count: 25,
    value: 110000
  }, {
    department: 'Sales',
    count: 18,
    value: 62500
  }, {
    department: 'HR',
    count: 12,
    value: 45000
  }, {
    department: 'Finance',
    count: 10,
    value: 38000
  }];
  const requisitionData = [{
    id: 'REQ-2023-001',
    name: 'Office Supplies',
    requestor: 'John Smith',
    department: 'Operations',
    status: 'Approved',
    date: '2023-10-01',
    value: 2500,
    aging: 12
  }, {
    id: 'REQ-2023-002',
    name: 'Software Licenses',
    requestor: 'Sarah Johnson',
    department: 'IT',
    status: 'Pending Approval',
    date: '2023-10-05',
    value: 8500,
    aging: 8
  }, {
    id: 'REQ-2023-003',
    name: 'Marketing Materials',
    requestor: 'David Lee',
    department: 'Marketing',
    status: 'Converted to PO',
    date: '2023-09-28',
    value: 4200,
    aging: 15
  }, {
    id: 'REQ-2023-004',
    name: 'Office Furniture',
    requestor: 'Emily Chen',
    department: 'HR',
    status: 'Draft',
    date: '2023-10-10',
    value: 12000,
    aging: 3
  }, {
    id: 'REQ-2023-005',
    name: 'Training Services',
    requestor: 'Michael Brown',
    department: 'Sales',
    status: 'Rejected',
    date: '2023-10-02',
    value: 5500,
    aging: 11
  }];
  const totalRequisitions = statusData.reduce((sum, item) => sum + item.value, 0);
  const totalValue = departmentData.reduce((sum, item) => sum + item.value, 0);
  const getStatusColor = status => {
    switch (status) {
      case 'Draft':
        return 'bg-gray-400';
      case 'Pending Approval':
        return 'bg-yellow-500';
      case 'Approved':
        return 'bg-green-500';
      case 'Rejected':
        return 'bg-red-500';
      case 'Converted to PO':
        return 'bg-blue-500';
      default:
        return 'bg-gray-400';
    }
  };
  return <div className="p-6" data-id="element-16654">
      <div className="mb-8" data-id="element-16655">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-16656">
          Requisition Summary Report
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-16657">
          Status and aging analysis by entity, user, or department
        </p>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-16658">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-16659">
          <div className="p-5" data-id="element-16660">
            <div className="flex items-center" data-id="element-16661">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-16662">
                <FileTextIcon className="h-6 w-6 text-white" data-id="element-16663" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-16664">
                <dl data-id="element-16665">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-16666">
                    Total Requisitions
                  </dt>
                  <dd data-id="element-16667">
                    <div className="text-lg font-medium text-gray-900" data-id="element-16668">
                      {totalRequisitions}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-16669">
          <div className="p-5" data-id="element-16670">
            <div className="flex items-center" data-id="element-16671">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-16672">
                <CheckCircleIcon className="h-6 w-6 text-white" data-id="element-16673" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-16674">
                <dl data-id="element-16675">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-16676">
                    Approval Rate
                  </dt>
                  <dd data-id="element-16677">
                    <div className="text-lg font-medium text-gray-900" data-id="element-16678">
                      {Math.round(statusData.find(s => s.name === 'Approved').value / totalRequisitions * 100)}
                      %
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-16679">
          <div className="p-5" data-id="element-16680">
            <div className="flex items-center" data-id="element-16681">
              <div className="flex-shrink-0 bg-purple-500 rounded-md p-3" data-id="element-16682">
                <ClockIcon className="h-6 w-6 text-white" data-id="element-16683" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-16684">
                <dl data-id="element-16685">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-16686">
                    Avg. Approval Time
                  </dt>
                  <dd data-id="element-16687">
                    <div className="text-lg font-medium text-gray-900" data-id="element-16688">
                      2.3 days
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-16689">
        <div className="bg-white p-6 rounded-lg shadow" data-id="element-16690">
          <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-16691">
            Requisition Status
          </h4>
          <div className="h-80" data-id="element-16692">
            <ResponsiveContainer width="100%" height="100%" data-id="element-16693">
              <PieChart data-id="element-16694">
                <Pie data={statusData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
                cx,
                cy,
                midAngle,
                innerRadius,
                outerRadius,
                percent,
                index
              }) => {
                const RADIAN = Math.PI / 180;
                const radius = 25 + innerRadius + (outerRadius - innerRadius);
                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                return <text x={x} y={y} fill="#000" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" data-id="element-16696">
                        {statusData[index].name} ({(percent * 100).toFixed(0)}%)
                      </text>;
              }} data-id="element-16695">
                  {statusData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-16697" />)}
                </Pie>
                <Tooltip data-id="element-16698" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow" data-id="element-16699">
          <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-16700">
            Requisitions by Department
          </h4>
          <div className="h-80" data-id="element-16701">
            <ResponsiveContainer width="100%" height="100%" data-id="element-16702">
              <BarChart data={departmentData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-16703">
                <CartesianGrid strokeDasharray="3 3" data-id="element-16704" />
                <XAxis dataKey="department" data-id="element-16705" />
                <YAxis yAxisId="left" orientation="left" stroke="#8884d8" data-id="element-16706" />
                <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" data-id="element-16707" />
                <Tooltip data-id="element-16708" />
                <Legend data-id="element-16709" />
                <Bar yAxisId="left" dataKey="count" name="Count" fill="#8884d8" data-id="element-16710" />
                <Bar yAxisId="right" dataKey="value" name="Value" fill="#82ca9d" data-id="element-16711" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Requisition Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-16712">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-16713">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-16714">
            Recent Requisitions
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-16715">
            Details of recent requisition activity
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-16716">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-16717">
            <thead className="bg-gray-50" data-id="element-16718">
              <tr data-id="element-16719">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16720">
                  Requisition
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16721">
                  Requestor / Department
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16722">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16723">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16724">
                  Value
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16725">
                  Aging (days)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-16726">
              {requisitionData.map(req => <tr key={req.id} data-id="element-16727">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-16728">
                    {req.name}
                    <div className="text-xs text-gray-500" data-id="element-16729">{req.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-16730">
                    {req.requestor}
                    <div className="text-xs text-gray-400" data-id="element-16731">
                      {req.department}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-16732">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(req.status)} text-white`} data-id="element-16733">
                      {req.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-16734">
                    {req.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-16735">
                    {formatCurrency(req.value, 'USD', locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-16736">
                    {req.aging}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}